import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { remove } from "../../Libraries/Ajax/HttpService";
import { useTheme } from "../../context/ThemeContext";
import { useDistrict } from "../../utils/DistrictContext";
import { FEED_BASE } from "./useDashboardFeeds";
import ModulesMenu from "../../components/mw2/ModulesMenu";

// The seven tabs, in the design's two groups. `native` marks the ones rebuilt
// in React; the rest are served by the original build (LegacyFrame).
export const TAB_GROUPS = [
  {
    label: "Intelligence",
    tabs: [
      { id: "alerts", label: "Mizu AI", icon: "fa-brain", accent: "var(--purple)", title: "Mizu AI — Operational Intelligence", native: true },
      { id: "twin", label: "Digital Twin", icon: "fa-diagram-project", accent: "var(--cyan)", title: "Digital Twin — live monitoring" },
      { id: "hydraulics", label: "Hydraulics", icon: "fa-gauge-high", accent: "var(--blue)", title: "Hydraulics — EPANET model" },
    ],
  },
  {
    label: "Operations",
    tabs: [
      { id: "plant", label: "Plant", icon: "fa-industry", accent: "var(--green)", title: "Plant — SCADA command" },
      { id: "efficiency", label: "Efficiency", icon: "fa-bolt", accent: "var(--amber)", title: "Efficiency — NRW, energy, risk" },
      { id: "workforce", label: "WorkForce", icon: "fa-users", accent: "var(--green)", title: "WorkForce — dispatch" },
      { id: "financial", label: "Financial", icon: "fa-sack-dollar", accent: "var(--orange)", title: "Financial — forecast & reporting" },
    ],
  },
];

export const isNativeTab = (id) =>
  TAB_GROUPS.some((group) => group.tabs.some((tab) => tab.id === id && tab.native));

// Open/closed state for a header dropdown that closes on a click anywhere
// else, or Escape.
function useDropdown() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return [open, setOpen, rootRef];
}

const THEMES = [
  { value: "blue", label: "Mizu", color: "#5ce1ff" },
  { value: "pastel", label: "Teal Green", color: "#4a9b8e" },
  { value: "white", label: "White", color: "#ffffff" },
];

function AccountMenu() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [open, setOpen, rootRef] = useDropdown();
  return (
    <div className="app-menu-wrap" ref={rootRef}>
      <button
        className={`control-btn${open ? " active" : ""}`}
        type="button"
        title="Account"
        aria-label="Account"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <i className="fa-solid fa-user" />
      </button>
      <div className={`app-menu-dropdown panel${open ? " is-open" : ""}`} role="menu" aria-hidden={!open}>
        <nav className="sidebar-nav">
          <button
            className="sidebar-nav-item"
            type="button"
            role="menuitem"
            tabIndex={open ? 0 : -1}
            onClick={() => {
              setOpen(false);
              navigate("/profile");
            }}
          >
            <i className="fa-solid fa-user-gear" />
            Profile Settings
          </button>
          <div className="app-menu-heading">Theme</div>
          {THEMES.map((t) => (
            <button
              key={t.value}
              className={`sidebar-nav-item${theme === t.value ? " active" : ""}`}
              type="button"
              role="menuitemradio"
              aria-checked={theme === t.value}
              tabIndex={open ? 0 : -1}
              onClick={() => setTheme(t.value)}
            >
              <span className="app-menu-swatch" style={{ background: t.color }} />
              {t.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return <span className="header-clock">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>;
}

// The design's header: brand, grouped tabs, and on the right its buttons -
// district, the map controls with the app menu among them, clock, account,
// home and logout. Every other page wears it too (AppHeader) - without map
// controls, so `controls` is left out there, and `hideDistrict` drops the
// district picker where a page has no use for it.
export default function DashboardHeader({ activeTab, onTab, badge, controls, onControl, hideDistrict = false }) {
  const navigate = useNavigate();
  const { selectedDistrict, setSelectedDistrict } = useDistrict() || {};
  // The districts this user can see, as stored at login.
  const [districts] = useState(() => {
    try {
      const list = JSON.parse(localStorage.getItem("MCData") || "{}")?.district;
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  });

  // The app header's district switch, as is: stored first, so the requests the
  // change sets off already carry the new District header.
  const changeDistrict = (name) => {
    const stored = JSON.parse(localStorage.getItem("MCData") || "{}");
    stored.selectedDistrict = name;
    localStorage.setItem("MCData", JSON.stringify(stored));
    setSelectedDistrict?.(name);
  };

  // The app header's logout, as is.
  const logout = () => {
    remove("apis/user_logout")
      .then(() => {
        localStorage.clear();
        toast.success("Logged out successfully");
        navigate("/login");
      })
      .catch(() => toast.error("Logout failed"));
  };

  return (
    <header className="header panel">
      <div className="brand">
        <div className="brand-mark">
          <img src={`${FEED_BASE}logosmallwhite.png`} alt="MizuWatch logo" />
        </div>
        <div>
          <h1 className="brand-title">
            Mizu<span>Watch</span> <span className="version-chip">3.3</span>
          </h1>
          <div className="brand-subtitle">Operational Intelligence · One platform</div>
        </div>
      </div>

      <nav className="htabs" aria-label="Dashboard sections">
        {TAB_GROUPS.map((group, index) => [
          index > 0 ? <span key={`divider-${group.label}`} className="htab-divider" aria-hidden="true" /> : null,
          <div className="htab-group" key={group.label}>
            <span className="htab-group-label">{group.label}</span>
            {group.tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`htab${activeTab === tab.id ? " active" : ""}`}
                style={{ "--tab-accent": tab.accent }}
                title={tab.title}
                onClick={() => onTab(tab.id)}
              >
                <i className={`fa-solid ${tab.icon}`} />
                <span className="htab-label">{tab.label}</span>
                {tab.id === "alerts" && badge != null ? <span className="htab-badge">{badge.toLocaleString()}</span> : null}
              </button>
            ))}
          </div>,
        ])}
      </nav>

      <div className="header-meta">
        {hideDistrict ? null : (
          <select
            className="district-select"
            aria-label="District"
            value={selectedDistrict || ""}
            onChange={(event) => changeDistrict(event.target.value)}
          >
            {/* A district the stored list does not name still shows as selected. */}
            {selectedDistrict && !districts.some((d) => d.district_name === selectedDistrict) ? (
              <option value={selectedDistrict}>{selectedDistrict}</option>
            ) : null}
            {districts.map((d) => (
              <option key={d.district_name} value={d.district_name}>
                {d.district_display_name || d.district_name}
              </option>
            ))}
          </select>
        )}
        {controls ? (
          <div className="control-strip">
            <button className={`control-btn${controls.flowOn ? " active" : ""}`} type="button" title="Toggle Flow Pipelines" onClick={() => onControl("flow")}>
              <i className="fa-solid fa-network-wired" />
            </button>
            <button className={`control-btn${controls.markersOn ? " active" : ""}`} type="button" title="Toggle Meter Markers" onClick={() => onControl("markers")}>
              <i className="fa-solid fa-location-dot" />
            </button>
            {/* The Modules menu, one of the header's buttons. */}
            <ModulesMenu />
            <button className={`control-btn${controls.styleKey === "satellite" ? " active" : ""}`} type="button" title="Switch Map Theme" onClick={() => onControl("style")}>
              <i className="fa-solid fa-earth-americas" />
            </button>
            <button className="control-btn" type="button" title="Zoom In" onClick={() => onControl("zoomIn")}>
              <i className="fa-solid fa-plus" />
            </button>
            <button className="control-btn" type="button" title="Zoom Out" onClick={() => onControl("zoomOut")}>
              <i className="fa-solid fa-minus" />
            </button>
          </div>
        ) : (
          // The other pages have no map buttons: the Modules menu stands
          // where they would, labelled with the page's group.
          <ModulesMenu />
        )}
        <Clock />
        <div className="control-strip">
          <AccountMenu />
          <button className="control-btn" type="button" title="Home - Mizu AI" aria-label="Home" onClick={() => navigate("/new-dashboard?tab=alerts")}>
            <i className="fa-solid fa-house" />
          </button>
          <button className="control-btn" type="button" title="Logout" aria-label="Logout" onClick={logout}>
            <i className="fa-solid fa-right-from-bracket" />
          </button>
        </div>
      </div>
    </header>
  );
}
